/* eslint-disable no-nested-ternary */
import React, { useMemo, useState, useEffect } from 'react';
import cx from 'classnames';
import moment from 'moment';
import Holidays from 'date-holidays';
import { useLazyQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import Loader from 'react-loader-spinner';
import { TimesArrayContext } from '../../../contexts/TimesArray';
import {
  getFirstWeekdayDateParts,
  getNextPacificWeekdayDateParts,
  getPacificTodayMoment,
} from '../../../helpers/pacificTime';
import {
  getBookingHourWindows,
  shouldDisplayTimeSlot,
} from '../../../helpers/bookingHours';

import styles from '../../../pages/Forms/Forms.scss';

import Calendar from '../../Calendar';

const hd = new Holidays('US');
const defaultPacificSelection = getNextPacificWeekdayDateParts(1);

const GET_TIMES = gql`
  query Calendar($date: CalendarInput!) {
    getCalendar(calendarInput: $date) {
      id
      times
    }
  }
`;

const CalendarPreviw = props => {
  const [isValid, setIsValid] = useState(true);
  const [timeLoading, setTimeLoading] = useState(true);
  const [times, setTimes] = useState();
  const timesArray = React.useContext(TimesArrayContext);

  const [selectedYear, setSelectedYear] = useState(defaultPacificSelection.year);
  const [selectedMonth, setSelectedMonth] = useState(
    defaultPacificSelection.month,
  );
  const [selectedDay, setSelectedDay] = useState(defaultPacificSelection.day);
  const [secondTurn, setSecondTurn] = useState(false);

  const [pickedTime, setPickedTime] = useState();

  const [
    getTimes,
    { loading: getTimesLoading, error, data: timesInfo },
  ] = useLazyQuery(GET_TIMES, {
    variables: {
      date: { year: selectedYear, month: selectedMonth, day: selectedDay },
    },
    fetchPolicy: 'network-only',
    onCompleted: res => {
      updateTimes(res.getCalendar);
    },
    onError: err => {
      console.log(err);
    },
  });

  const updateTimes = async res => {
    const saturday = await await moment(
      `${selectedYear}-${selectedMonth}`,
      'YYYY-MM',
    )
      .startOf('month')
      .day('Saturday');
    const sunday = await await moment(
      `${selectedYear}-${selectedMonth}`,
      'YYYY-MM',
    )
      .startOf('month')
      .day('Sunday');

    const weekendsInMonth = [];
    if (saturday.date() > 7) saturday.add(7, 'd');
    while (selectedMonth - 1 === saturday.month()) {
      weekendsInMonth.push(saturday.date());
      saturday.add(7, 'd');
    }

    if (sunday.date() > 7) sunday.add(7, 'd');
    while (selectedMonth - 1 === sunday.month()) {
      weekendsInMonth.push(sunday.date());
      sunday.add(7, 'd');
    }

    const bookedIds = [];
    const updatedTimes = [];

    await res.forEach(booking => {
      booking.times ? booking.times.map(time => bookedIds.push(time)) : null;
    });

    const isHoliday = await hd.isHoliday(
      new Date(`${selectedYear}-${selectedMonth}-${selectedDay} 10:00:00`),
    );
    const hourWindows = getBookingHourWindows(
      selectedYear,
      selectedMonth,
      selectedDay,
    );

    await timesArray.forEach(time => {
      // Same start-window filter as the tour booking calendar (through 7:00 PM;
      // Thursdays open at 1:00 PM). Keeps homepage preview aligned with the form.
      if (!shouldDisplayTimeSlot(time.label, hourWindows, 'tour')) {
        return;
      }

      const isBooked = bookedIds.includes(time.id);
      const isWeekend = weekendsInMonth.includes(selectedDay);
      const holidayWeekendLabel = isWeekend
        ? 'Weekend'
        : isHoliday
        ? isHoliday.name
        : null;
      const noBuffer = isBooked
        ? false
        : bookedIds.includes(time.id + 1) || bookedIds.includes(time.id + 2);

      updatedTimes.push(
        <button
          key={time.label}
          disabled={isBooked || noBuffer || isWeekend}
          label={holidayWeekendLabel}
          className={cx(
            !isHoliday && !isHoliday && noBuffer ? styles.no__buffer : '',
            time.id === pickedTime ? styles.selected : '',
            isWeekend || isHoliday ? styles.holiday : '',
          )}
        >
          {time.label}
        </button>,
      );
    });

    setTimes(updatedTimes);
  };

  const isBefore = (y, m) => {
    const selectedDate = moment([y, m]);
    const todayDate = getPacificTodayMoment().startOf('month');

    const beforeYear = todayDate.isAfter(selectedDate, 'years');
    const beforeMonth = selectedDate.isBefore(todayDate, 'months');
    return beforeMonth || beforeYear;
  };
  const changeDateHandler = dir => {
    setPickedTime(null);
    setSecondTurn(false);
    const isPast = isBefore(selectedYear, selectedMonth - 2);
    if (dir === 'next') {
      const targetYear = selectedMonth === 12 ? selectedYear + 1 : selectedYear;
      const targetMonth = selectedMonth === 12 ? 1 : selectedMonth + 1;
      const targetDate = getFirstWeekdayDateParts(targetYear, targetMonth);

      if (selectedMonth === 12) {
        setSelectedYear(targetDate.year);
        setSelectedMonth(targetDate.month);
      } else {
        setSelectedMonth(targetDate.month);
      }
      setSelectedDay(targetDate.day);
      getTimes();
    } else if (dir === 'prev') {
      const defaultDate = getNextPacificWeekdayDateParts(1);
      const targetYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
      const targetMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
      const targetDate =
        targetYear === defaultDate.year && targetMonth === defaultDate.month
          ? defaultDate
          : getFirstWeekdayDateParts(targetYear, targetMonth);

      if (!isPast) {
        setSelectedDay(targetDate.day);
      }
      if (selectedMonth === 1) {
        setSelectedYear(targetDate.year);
        setSelectedMonth(targetDate.month);
        getTimes();
      } else if (!isPast) {
        setSelectedMonth(targetDate.month);
        getTimes();
      }
    }
  };
  useEffect(() => {
    if (!timesInfo) {
      getTimes();
    }

    !getTimesLoading
      ? setTimeout(() => {
          setTimeLoading(false);
        }, 1000)
      : setTimeLoading(true);
  }, [
    getTimesLoading,
    selectedDay,
    selectedMonth,
    selectedYear,
    times,
    pickedTime,
    isValid,
    timeLoading,
  ]);
  return (
    <>
      <div className={cx(styles.calendar__wrapper, styles.preview)}>
        <div className={styles.calendar__cal}>
          <div className={styles.calendar__cal__nav}>
            <a
              role="button"
              tabIndex="0"
              onClick={() => changeDateHandler('prev')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 451.846 451.847"
              >
                <path d="M345.441 248.292L151.154 442.573c-12.359 12.365-32.397 12.365-44.75 0-12.354-12.354-12.354-32.391 0-44.744L278.318 225.92 106.409 54.017c-12.354-12.359-12.354-32.394 0-44.748 12.354-12.359 32.391-12.359 44.75 0l194.287 194.284c6.177 6.18 9.262 14.271 9.262 22.366 0 8.099-3.091 16.196-9.267 22.373z" />
              </svg>
            </a>
            <a
              role="button"
              tabIndex="0"
              onClick={() => changeDateHandler('next')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 451.846 451.847"
              >
                <path d="M345.441 248.292L151.154 442.573c-12.359 12.365-32.397 12.365-44.75 0-12.354-12.354-12.354-32.391 0-44.744L278.318 225.92 106.409 54.017c-12.354-12.359-12.354-32.394 0-44.748 12.354-12.359 32.391-12.359 44.75 0l194.287 194.284c6.177 6.18 9.262 14.271 9.262 22.366 0 8.099-3.091 16.196-9.267 22.373z" />
              </svg>
            </a>
          </div>
          <Calendar
            year={selectedYear}
            month={selectedMonth}
            selectedDay={selectedDay}
            setSelectedDay={setSelectedDay}
            preview
            disableWeekends
          />
          
        </div>
        <div className={styles.calendar__time}>
          {timeLoading ? (
            <div className={styles.calendar__time__wrapper__loader}>
              <Loader type="Grid" color="#fff" height={30} width={30} />
            </div>
          ) : (
            <>
              <div className={styles.calendar__time__wrapper}>
                {times && times.map(time => time)}
              </div>
              <div className={styles.calendar__time__guide}>
                <div className={styles.available}>
                  <span />
                  <span>Available</span>
                </div>
                <div className={styles.not_available}>
                  <span />
                  <span>Not Available</span>
                </div>
                <div className={styles.booked}>
                  <span />
                  <span>Booked/Closed</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CalendarPreviw;
